import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Plus } from 'lucide-react'
import { Permit } from '@/components/guards/Permit'
import { FORM_CONTROL_FOCUS_CLASS } from '@/components/ui/form-control-styles'
import { Skeleton } from '@/components/ui/skeleton'
import { ListPagination } from '@/components/shared/ListPagination'
import { JobStatusBadge } from '@/components/shared/StatusBadges'
import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/format'
import { JOB_STATUS, type ApiList, type Job, type JobStatus } from '@/types'
import clockIcon from '@/assets/contracts/detail/clock-header.svg'
import checkIcon from '@/assets/contracts/detail/check.svg'

const cardShadow =
  'shadow-[0px_8px_16px_-4px_rgba(38,45,66,0.1),0px_1px_4px_-4px_rgba(38,45,66,0.05)]'

function formatStamp(job: Job, language: string) {
  const date = formatDate(job.scheduled_date)
  if (!job.start_time) return date
  const [h, m] = job.start_time.split(':')
  if (h == null || Number.isNaN(Number(h))) return date
  const d = new Date()
  d.setHours(Number(h), Number(m ?? 0), 0, 0)
  const locale = language.startsWith('fr') ? 'fr-FR' : 'en-US'
  const time = d.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: Number(m) ? '2-digit' : undefined,
  })
  return `${date} ${time}`
}

function jobPlace(job: Job) {
  return [job.postal_code, job.city].filter(Boolean).join(' ')
}

function MetaDot() {
  return (
    <span className='size-1 shrink-0 rounded-full bg-[#b0b5c3]' aria-hidden />
  )
}

function TimelineMarker({
  status,
  last,
}: {
  status: JobStatus
  last: boolean
}) {
  const completed = status === JOB_STATUS.completed
  return (
    <div className='relative flex h-6 w-6 shrink-0 flex-col items-center'>
      <span
        className={cn(
          'flex size-6 shrink-0 items-center justify-center rounded-full border-2 p-0.5',
          completed ? 'border-[#ecfdf5]' : 'border-[#fff1ea]'
        )}
      >
        <span
          className={cn(
            'flex size-4 items-center justify-center rounded-full',
            completed ? 'bg-[#10b981]' : 'bg-[#ff5a1f]'
          )}
        >
          {completed ? (
            <img
              src={checkIcon}
              alt=''
              width={11}
              height={8}
              className='h-[8px] w-[11px] max-w-none'
            />
          ) : (
            <span className='size-2 rounded-full bg-white' />
          )}
        </span>
      </span>
      {!last && (
        <span
          className='absolute left-1/2 top-[26px] h-9 w-px -translate-x-1/2 bg-[#e7e9ef]'
          aria-hidden
        />
      )}
    </div>
  )
}

// Figma 3480:85532 — Interventions tab card.
export function ContractDetailInterventionsCard({
  data,
  isLoading,
  isError,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
  canSchedule,
  onSchedule,
  onOpenJob,
}: {
  data: ApiList<Job> | undefined
  isLoading: boolean
  isError: boolean
  page: number
  pageSize: number
  onPageChange: (p: number) => void
  onPageSizeChange: (s: number) => void
  canSchedule: boolean
  onSchedule: () => void
  onOpenJob: (jobId: string) => void
}) {
  const { t, i18n } = useTranslation('contracts')
  const navigate = useNavigate()
  const rows = data?.data ?? []
  const total = data?.metadata?.total ?? rows.length

  return (
    <section
      className={cn(
        'flex flex-col overflow-hidden rounded-2xl border border-[#e7e9ef] bg-white',
        cardShadow
      )}
    >
      <div className='flex h-16 items-center gap-2 px-6 pb-2 pt-6'>
        <div className='flex min-w-0 flex-1 items-center gap-3'>
          <span className='flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#f5f6fa] p-2'>
            <img
              src={clockIcon}
              alt=''
              width={16}
              height={16}
              className='size-4 max-w-none'
            />
          </span>
          <h2 className='font-heading truncate text-sm font-semibold leading-[1.2] text-[#171a20]'>
            {t('detail.interventions.title', { count: total })}
          </h2>
        </div>
        <button
          type='button'
          onClick={() => navigate('/calendar')}
          className={cn(
            'inline-flex h-8 shrink-0 items-center gap-2 rounded-lg p-2 text-xs font-medium leading-none text-[#ff5a1f] normal-case hover:bg-[#fff1ea]',
            FORM_CONTROL_FOCUS_CLASS
          )}
        >
          {t('detail.interventions.openAgenda')}
          <ArrowRight className='size-4' />
        </button>
        {canSchedule ? (
          <Permit module='contracts' action='edit'>
            <button
              type='button'
              onClick={onSchedule}
              className={cn(
                'inline-flex h-8 shrink-0 items-center gap-2 rounded-lg border border-[#ff5a1f] bg-[#ff5a1f] py-2 pl-2 pr-3 text-sm font-medium leading-none text-white normal-case hover:opacity-90',
                FORM_CONTROL_FOCUS_CLASS
              )}
            >
              <Plus className='size-4' />
              {t('detail.interventions.plan')}
            </button>
          </Permit>
        ) : null}
      </div>

      <div className='flex flex-col gap-4 px-6 pb-6 pt-4'>
        {isLoading &&
          Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className='h-12 w-full rounded-xl' />
          ))}
        {isError && !isLoading && (
          <p className='py-6 text-center text-sm text-destructive'>
            {t('detail.interventions.loadError')}
          </p>
        )}
        {!isLoading && !isError && rows.length === 0 && (
          <p className='py-6 text-center text-sm text-[#3d434e]'>
            {t('detail.interventions.empty')}
          </p>
        )}
        {!isLoading &&
          !isError &&
          rows.map((job, i) => {
            const tech = job.assigned_technicians?.[0]?.full_name
            const place = jobPlace(job)
            const extra = job.note?.trim() || null
            const showPlan = job.status === JOB_STATUS.completed && !!extra
            return (
              <button
                key={job.id}
                type='button'
                className={cn(
                  'group relative flex min-h-12 w-full items-center gap-4 rounded-lg px-3 py-2 text-left hover:bg-[#f4f5f8]',
                  FORM_CONTROL_FOCUS_CLASS
                )}
                onClick={() => onOpenJob(job.id)}
              >
                <span
                  className='absolute inset-y-2 left-0 hidden w-1 rounded-r bg-[#ff5a1f] group-hover:block'
                  aria-hidden
                />
                <TimelineMarker
                  status={job.status}
                  last={i === rows.length - 1}
                />
                <div className='flex min-w-0 flex-1 flex-col gap-1'>
                  <div className='flex flex-wrap items-center gap-3'>
                    <p className='text-sm font-medium leading-[1.4] text-[#171a20] normal-case group-hover:text-[#ff5a1f]'>
                      {job.service_type?.trim() ||
                        job.reference ||
                        t('detail.tabs.interventions')}
                    </p>
                    <JobStatusBadge
                      status={job.status}
                      label={t(`enums:jobStatus.${job.status}`, {
                        defaultValue: job.status.replace(/_/g, ' '),
                      })}
                      className='h-6 capitalize'
                    />
                  </div>
                  <div className='flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-normal leading-4 text-[#3d434e] normal-case'>
                    <span>{formatStamp(job, i18n.language)}</span>
                    {tech && (
                      <>
                        <MetaDot />
                        <span>{tech}</span>
                      </>
                    )}
                    {place && (
                      <>
                        <MetaDot />
                        <span>{place}</span>
                      </>
                    )}
                    {extra && (
                      <>
                        <MetaDot />
                        <span>{extra}</span>
                      </>
                    )}
                    {showPlan && (
                      <span className='inline-flex h-5 items-center gap-2 p-0.5 text-xs font-medium leading-none text-[#ff5a1f] normal-case group-hover:underline'>
                        {t('detail.recentInterventions.viewPlan')}
                        <ArrowRight className='size-4' />
                      </span>
                    )}
                  </div>
                </div>
              </button>
            )
          })}
      </div>

      <ListPagination
        className='border-t border-[#e7e9ef] pl-4 pr-6'
        metadata={data?.metadata}
        page={page}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        showShowingRange={false}
      />
    </section>
  )
}
